import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { FileSpreadsheet, FileText, Download, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

const EXPORT_SECTIONS = [
  { id: 'kpis', label: 'KPI Summary', description: 'Total Production, Active Machines, Records, Best Month' },
  { id: 'monthlyTrendChart', label: 'Monthly Production Trend (Chart)', description: 'Line chart image of monthly production' },
  { id: 'yearlyComparisonChart', label: 'Yearly Comparison (Chart)', description: 'Bar chart image of yearly production' },
  { id: 'monthlyTrend', label: 'Monthly Production Data (Table)', description: 'Monthly data in table format' },
  { id: 'yearlyComparison', label: 'Yearly Comparison Data (Table)', description: 'Yearly data in table format' },
  { id: 'last12Months', label: 'Last 12 Months Production', description: 'Bar chart of recent months' },
  { id: 'dailyProduction', label: 'Daily Production', description: 'Daily production chart' },
  { id: 'machineGroupTable', label: 'Production by Machine Group', description: 'Group-wise summary table' },
  { id: 'machineTable', label: 'Production by Machine', description: 'Machine-wise summary table' },
  { id: 'monthlyPivot', label: 'Machine Monthly Pivot', description: 'Detailed monthly breakdown' },
];

export const ExportReportDialog = ({ 
  open, 
  onClose, 
  dashboardData,
  dateRange,
  selectedGroups 
}) => {
  const [selectedSections, setSelectedSections] = useState(EXPORT_SECTIONS.map(s => s.id));
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState(null);

  const toggleSection = (sectionId) => {
    setSelectedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(s => s !== sectionId)
        : [...prev, sectionId]
    );
  };

  const selectAll = () => setSelectedSections(EXPORT_SECTIONS.map(s => s.id));
  const selectNone = () => setSelectedSections([]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDateRangeText = () => {
    if (dateRange?.from && dateRange?.to) {
      return `${formatDate(dateRange.from)} to ${formatDate(dateRange.to)}`;
    }
    return 'All Time';
  };

  const exportToPDF = async () => {
    setExporting(true);
    setExportType('pdf');
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Header
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Production Dashboard Report', margin, yPos);
      yPos += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100);
      pdf.text(`Generated: ${new Date().toLocaleString('en-IN')}`, margin, yPos);
      yPos += 5;
      pdf.text(`Date Range: ${getDateRangeText()}`, margin, yPos);
      yPos += 5;
      pdf.text(`Machine Groups: ${selectedGroups?.includes('all') ? 'All Groups' : selectedGroups?.join(', ') || 'All'}`, margin, yPos);
      yPos += 10;
      pdf.setTextColor(0);

      // KPIs Section
      if (selectedSections.includes('kpis') && dashboardData?.stats) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Key Performance Indicators', margin, yPos);
        yPos += 8;

        const kpiData = [
          ['Total Production', `${dashboardData.stats.total_production?.toLocaleString() || 0} MT`],
          ['Active Machines', `${dashboardData.stats.machine_count || 0}`],
          ['Total Records', `${dashboardData.stats.total_records?.toLocaleString() || 0}`],
          ['Best Month', dashboardData.bestMonth?.period ? `${dashboardData.bestMonth.period} (${Math.round((dashboardData.bestMonth.total_kg || 0) / 1000).toLocaleString()} MT)` : 'N/A'],
        ];

        autoTable(pdf, {
          startY: yPos,
          head: [['Metric', 'Value']],
          body: kpiData,
          theme: 'striped',
          headStyles: { fillColor: [41, 128, 185] },
          margin: { left: margin, right: margin },
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }

      // Monthly Production Trend Chart (Image)
      if (selectedSections.includes('monthlyTrendChart')) {
        const monthlyChartElement = document.querySelector('[data-testid="monthly-chart-card"]');
        if (monthlyChartElement) {
          if (yPos > pageHeight - 100) {
            pdf.addPage();
            yPos = margin;
          }

          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Monthly Production Trend', margin, yPos);
          yPos += 8;

          try {
            const canvas = await html2canvas(monthlyChartElement, {
              scale: 1.5,
              backgroundColor: '#ffffff',
              logging: false,
            });
            const imgData = canvas.toDataURL('image/jpeg', 0.8);
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            // Check if image fits on current page
            if (yPos + imgHeight > pageHeight - margin) {
              pdf.addPage();
              yPos = margin;
            }
            
            pdf.addImage(imgData, 'JPEG', margin, yPos, imgWidth, imgHeight);
            yPos += imgHeight + 10;
          } catch (err) {
            console.error('Error capturing monthly chart:', err);
            toast.error('Failed to capture Monthly Trend chart');
          }
        }
      }

      // Yearly Comparison Chart (Image)
      if (selectedSections.includes('yearlyComparisonChart')) {
        const yearlyChartElement = document.querySelector('[data-testid="yearly-chart-card"]');
        if (yearlyChartElement) {
          if (yPos > pageHeight - 100) {
            pdf.addPage();
            yPos = margin;
          }

          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Yearly Comparison', margin, yPos);
          yPos += 8;

          try {
            const canvas = await html2canvas(yearlyChartElement, {
              scale: 1.5,
              backgroundColor: '#ffffff',
              logging: false,
            });
            const imgData = canvas.toDataURL('image/jpeg', 0.8);
            const imgWidth = (pageWidth - (margin * 2)) * 0.6; // Smaller width for bar chart
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            if (yPos + imgHeight > pageHeight - margin) {
              pdf.addPage();
              yPos = margin;
            }
            
            pdf.addImage(imgData, 'JPEG', margin, yPos, imgWidth, imgHeight);
            yPos += imgHeight + 10;
          } catch (err) {
            console.error('Error capturing yearly chart:', err);
            toast.error('Failed to capture Yearly Comparison chart');
          }
        }
      }

      // Machine Group Table
      if (selectedSections.includes('machineGroupTable') && dashboardData?.machineGroupData?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Production by Machine Group', margin, yPos);
        yPos += 8;

        const groupData = dashboardData.machineGroupData.map(g => [
          g.machine_group || 'Unknown',
          `${Math.round(g.total_mt || 0).toLocaleString()} MT`
        ]);

        autoTable(pdf, {
          startY: yPos,
          head: [['Machine Group', 'Production (MT)']],
          body: groupData,
          theme: 'striped',
          headStyles: { fillColor: [41, 128, 185] },
          margin: { left: margin, right: margin },
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }

      // Machine Table
      if (selectedSections.includes('machineTable') && dashboardData?.machineData?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Production by Machine', margin, yPos);
        yPos += 8;

        const machineTableData = dashboardData.machineData.slice(0, 30).map(m => [
          m.machine || 'Unknown',
          m.machine_group || 'Unknown',
          `${Math.round(m.total_mt || 0).toLocaleString()} MT`
        ]);

        autoTable(pdf, {
          startY: yPos,
          head: [['Machine', 'Group', 'Production (MT)']],
          body: machineTableData,
          theme: 'striped',
          headStyles: { fillColor: [41, 128, 185] },
          margin: { left: margin, right: margin },
          styles: { fontSize: 8 },
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }

      // Monthly Data
      if (selectedSections.includes('monthlyTrend') && dashboardData?.monthlyData?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Monthly Production Data', margin, yPos);
        yPos += 8;

        const monthlyTableData = dashboardData.monthlyData.map(m => [
          m.month || m.period || 'Unknown',
          `${(m.total_mt || 0).toLocaleString()} MT`
        ]);

        autoTable(pdf, {
          startY: yPos,
          head: [['Month', 'Production (MT)']],
          body: monthlyTableData,
          theme: 'striped',
          headStyles: { fillColor: [41, 128, 185] },
          margin: { left: margin, right: margin },
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }

      // Yearly Data
      if (selectedSections.includes('yearlyComparison') && dashboardData?.yearlyData?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Yearly Production Comparison', margin, yPos);
        yPos += 8;

        const yearlyTableData = dashboardData.yearlyData.map(y => [
          y.year?.toString() || 'Unknown',
          `${(y.total_mt || 0).toLocaleString()} MT`
        ]);

        autoTable(pdf, {
          startY: yPos,
          head: [['Year', 'Production (MT)']],
          body: yearlyTableData,
          theme: 'striped',
          headStyles: { fillColor: [41, 128, 185] },
          margin: { left: margin, right: margin },
        });
      }

      // Footer on each page
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(
          `Page ${i} of ${pageCount} | Simplex Production Dashboard`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Save
      const filename = `Production_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
      setExportType(null);
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    setExportType('excel');

    try {
      const workbook = XLSX.utils.book_new();

      // KPIs Sheet
      if (selectedSections.includes('kpis') && dashboardData?.stats) {
        const kpiData = [
          ['Production Dashboard Report'],
          [`Generated: ${new Date().toLocaleString('en-IN')}`],
          [`Date Range: ${getDateRangeText()}`],
          [],
          ['Key Performance Indicators'],
          ['Metric', 'Value'],
          ['Total Production (MT)', dashboardData.stats.total_production || 0],
          ['Active Machines', dashboardData.stats.machine_count || 0],
          ['Total Records', dashboardData.stats.total_records || 0],
          ['Best Month', dashboardData.bestMonth?.period ? `${dashboardData.bestMonth.period}` : 'N/A'],
          ['Best Month Production (MT)', dashboardData.bestMonth?.total_kg ? Math.round(dashboardData.bestMonth.total_kg / 1000) : 0],
        ];
        const kpiSheet = XLSX.utils.aoa_to_sheet(kpiData);
        XLSX.utils.book_append_sheet(workbook, kpiSheet, 'Summary');
      }

      // Machine Group Sheet
      if (selectedSections.includes('machineGroupTable') && dashboardData?.machineGroupData?.length > 0) {
        const groupHeaders = ['Machine Group', 'Production (MT)'];
        const groupData = dashboardData.machineGroupData.map(g => [
          g.machine_group || 'Unknown',
          Math.round(g.total_mt || 0)
        ]);
        const groupSheet = XLSX.utils.aoa_to_sheet([groupHeaders, ...groupData]);
        XLSX.utils.book_append_sheet(workbook, groupSheet, 'By Machine Group');
      }

      // Machine Sheet
      if (selectedSections.includes('machineTable') && dashboardData?.machineData?.length > 0) {
        const machineHeaders = ['Machine', 'Machine Group', 'Production (MT)'];
        const machineData = dashboardData.machineData.map(m => [
          m.machine || 'Unknown',
          m.machine_group || 'Unknown',
          Math.round(m.total_mt || 0)
        ]);
        const machineSheet = XLSX.utils.aoa_to_sheet([machineHeaders, ...machineData]);
        XLSX.utils.book_append_sheet(workbook, machineSheet, 'By Machine');
      }

      // Monthly Data Sheet
      if (selectedSections.includes('monthlyTrend') && dashboardData?.monthlyData?.length > 0) {
        const monthlyHeaders = ['Month', 'Production (MT)'];
        const monthlyData = dashboardData.monthlyData.map(m => [
          m.month || m.period || 'Unknown',
          m.total_mt || 0
        ]);
        const monthlySheet = XLSX.utils.aoa_to_sheet([monthlyHeaders, ...monthlyData]);
        XLSX.utils.book_append_sheet(workbook, monthlySheet, 'Monthly Trend');
      }

      // Yearly Data Sheet
      if (selectedSections.includes('yearlyComparison') && dashboardData?.yearlyData?.length > 0) {
        const yearlyHeaders = ['Year', 'Production (MT)'];
        const yearlyData = dashboardData.yearlyData.map(y => [
          y.year,
          y.total_mt || 0
        ]);
        const yearlySheet = XLSX.utils.aoa_to_sheet([yearlyHeaders, ...yearlyData]);
        XLSX.utils.book_append_sheet(workbook, yearlySheet, 'Yearly Comparison');
      }

      // Daily Production Sheet
      if (selectedSections.includes('dailyProduction') && dashboardData?.dailyData?.length > 0) {
        const dailyHeaders = ['Date', 'Production (MT)'];
        const dailyData = dashboardData.dailyData.map(d => [
          d.day || d.date || 'Unknown',
          d.total_mt || 0
        ]);
        const dailySheet = XLSX.utils.aoa_to_sheet([dailyHeaders, ...dailyData]);
        XLSX.utils.book_append_sheet(workbook, dailySheet, 'Daily Production');
      }

      // Last 12 Months Sheet
      if (selectedSections.includes('last12Months') && dashboardData?.last12MonthsData?.length > 0) {
        const last12Headers = ['Month', 'Production (MT)'];
        const last12Data = dashboardData.last12MonthsData.map(m => [
          m.month || m.period || 'Unknown',
          m.total_mt || 0
        ]);
        const last12Sheet = XLSX.utils.aoa_to_sheet([last12Headers, ...last12Data]);
        XLSX.utils.book_append_sheet(workbook, last12Sheet, 'Last 12 Months');
      }

      // Generate and save
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Production_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, filename);
      toast.success('Excel exported successfully!');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
      setExportType(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Dashboard Report
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium">Select sections to export:</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs h-7">
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={selectNone} className="text-xs h-7">
                Clear
              </Button>
            </div>
          </div>

          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
            {EXPORT_SECTIONS.map(section => (
              <label
                key={section.id}
                className="flex items-start gap-3 p-2 rounded-md hover:bg-muted cursor-pointer"
              >
                <Checkbox
                  checked={selectedSections.includes(section.id)}
                  onCheckedChange={() => toggleSection(section.id)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">{section.label}</div>
                  <div className="text-xs text-muted-foreground">{section.description}</div>
                </div>
              </label>
            ))}
          </div>

          {selectedSections.length === 0 && (
            <p className="text-sm text-destructive mt-2">Please select at least one section to export</p>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={exporting}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={exportToExcel}
            disabled={exporting || selectedSections.length === 0}
            className="flex items-center gap-2"
            data-testid="export-excel-button"
          >
            {exporting && exportType === 'excel' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4 text-green-600" />
            )}
            Excel
          </Button>
          <Button
            onClick={exportToPDF}
            disabled={exporting || selectedSections.length === 0}
            className="flex items-center gap-2"
            data-testid="export-pdf-button"
          >
            {exporting && exportType === 'pdf' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
